import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';
import {
  MINIMUM_PRODUCT_RATING,
  type Product,
  type ProductRating
} from '@/domain/Product';
import useMarketConfig from '@/widgets/hooks/useMarketConfig';
import useRoute from '@/widgets/hooks/useRoute';

export type ProductJsonLdProps = {
  product: Product;
};

/**
 * @see https://nestle.atlassian.net/wiki/spaces/EUXUI/pages/70289454/Structured+data+JSON-LD
 */
export default function ProductJsonLd({ product }: ProductJsonLdProps) {
  const marketConfig = useMarketConfig();
  const { market, locale, productTypePath, technology, slug } = useRoute();

  const meta = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    sku: product.id,
    mpn: product.nessoft.legacyId,
    name: product.name,
    image: resolveRelativeUrl(product.mainImage.url),
    description: product.seo.description,
    aggregateRating: ratingSchema(product.rating),
    offers: {
      '@type': 'Offer',
      url: resolveRelativeUrl(
        `/${market}/${locale}/order/${productTypePath}/${technology}/${slug}`
      ),
      availability: `https://schema.org/${product.inStock ? 'InStock' : 'OutOfStock'}`,
      price: product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER,
      priceCurrency: marketConfig.financialSettings.defaultCurrency
    }
  };

  return <script type="application/ld+json">{JSON.stringify(meta)}</script>;
}

function resolveRelativeUrl(imagePath: string): string {
  return new URL(
    imagePath,
    typeof document !== 'undefined'
      ? document.location.href
      : 'https://nespresso.com' // SSR Fallback
  ).href;
}

function ratingSchema(rating?: ProductRating) {
  return rating?.ratingAverage &&
    rating.reviewCount &&
    rating.ratingAverage >= MINIMUM_PRODUCT_RATING
    ? {
        '@type': 'AggregateRating',
        ratingValue: rating.ratingAverage?.toFixed(2),
        reviewCount: rating.reviewCount
      }
    : undefined;
}
